import React, { useCallback } from 'react';
import type { FormikHelpers } from 'formik';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import * as Yup from 'yup';

import { UI_OPERATION_STATUS_REQUEST } from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import {
  FormikWrapper,
  FormEmail as EmailField,
  FormTextField as TextField,
} from '@uncinc/uncinc-react-kitchen-sink';
import { useExtendedFormikContext } from '@uncinc/uncinc-react-kitchen-sink/dist/components/Form/FormikWrapper';

import { Button } from '../../../components';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import useRoutes from '../../../hooks/useRoutes';
import useToast from '../../../hooks/useToast';
import useDispatch from '../../../hooks/useTypedDispatch';
import { userUpdateWithHti } from '../../../store/actions/User';

interface FormValues {
  name: string;
  email: string;
}

type FormHelpers = FormikHelpers<FormValues>;

const OnboardingHtiFormInner: React.FC = () => {
  const formik = useExtendedFormikContext() as FormHelpers;
  const { t } = useTranslation();
  const { buildClass } = useClasses('PageOnboarding__form');
  const userUpdateOperationStatus = useOperationStatus('user', 'update');

  const handleSkip = useCallback(() => {
    // Reset any values already set by the user, so we don't submit them accidentally.
    // eslint-disable-next-line no-void
    void formik.setValues({ name: '', email: '' });
    // eslint-disable-next-line no-void
    void formik.submitForm();
  }, [formik]);

  return (
    <>
      <TextField
        fieldName="name"
        title={t('pages.onboarding_credentials_hti.fields.fullName.label')}
        placeholder={t('pages.onboarding_credentials_hti.fields.fullName.placeholder')}
      />
      <EmailField
        fieldName="email"
        title={t('pages.onboarding_credentials_hti.fields.email.label')}
        placeholder={t('pages.onboarding_credentials_hti.fields.email.placeholder')}
      />
      <div className={buildClass('buttons')}>
        <Button
          className={buildClass('submit')}
          htmlType="submit"
          buttonState={userUpdateOperationStatus === UI_OPERATION_STATUS_REQUEST ? 'disabled' : undefined}
        >
          {t('pages.onboarding_credentials_hti.submit')}
        </Button>

        <Button
          secondary
          onClick={handleSkip}
        >
          {t('pages.onboarding_credentials_hti.skip')}
        </Button>
      </div>
    </>
  );
};

const OnboardingHtiForm: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const { program } = useRoutes();
  const toast = useToast();

  const userId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid'], null));

  const validationSchema = Yup.object().shape({
    mail: Yup.string()
      .email(t('pages.onboarding_credentials_hti.fields.email.validation.email')),
  });

  const handleSubmit = useCallback(({ name, email }: FormValues) => {
    if (!userId) {
      return;
    }

    // Format the values to REST api standards.
    const values = {
      ...(name && {
        field_name: [{ value: name }],
      }),
      ...(email && {
        email: [{ value: email }],
      }),
      field_hti_newly_registered: [{ value: false }],
    };

    dispatch(userUpdateWithHti(userId, values))
      .then(() => {
        history.push(program);
      })
      .catch(() => {
        toast.show(t('pages.onboarding_credentials_hti.error'), 'error');
      });
  }, [dispatch, history, program, t, toast, userId]);

  return (
    <FormikWrapper
      initialValues={{ name: '', email: '' }}
      validationSchema={validationSchema}
      onSubmit={(values: FormValues, { setSubmitting }: FormHelpers) => {
        handleSubmit(values);
        setSubmitting(false);
      }}
      validateOnBlur={false}
      validateOnChange={false}
    >
      <OnboardingHtiFormInner />
    </FormikWrapper>
  );
};

export default OnboardingHtiForm;
